import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ClientAuthProvider } from './contexts/ClientAuthContext';
import { LicenceProvider } from './contexts/LicenceContext';
import { Toaster } from './components/ui/sonner';
import WhatsAppHelpButton from './components/WhatsAppHelpButton';
import Sidebar from './components/Sidebar';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import GetStartedPage from './pages/GetStartedPage';
import ClientLoginPage from './pages/ClientLoginPage';
import MyBookingsPage from './pages/MyBookingsPage';
import DashboardPage from './pages/DashboardPage';
import AppointmentsPage from './pages/AppointmentsPage';
import ClientsPage from './pages/ClientsPage';
import ClientProfilePage from './pages/ClientProfilePage';
import ServicesPage from './pages/ServicesPage';
import StaffPage from './pages/StaffPage';
import ReportsPage from './pages/ReportsPage';
import SalesPage from './pages/SalesPage';
import RetentionPage from './pages/RetentionPage';
import MarketingPage from './pages/MarketingPage';
import SettingsPage from './pages/SettingsPage';
import LocationsPage from './pages/LocationsPage';
import ResourcesPage from './pages/ResourcesPage';
import SuperAdminPage from './pages/SuperAdminPage';
import AdminLoginPage from './pages/AdminLoginPage';
import PublicBookingPage from './pages/PublicBookingPage';
import MobileBottomNav from './components/MobileBottomNav';
import MorePage from './pages/MorePage';
import AIAssistantPage from './pages/AIAssistantPage';
import HelpPage from './pages/HelpPage';
import PushNotificationManager from './components/PushNotificationManager';
import './App.css';
import TrialBanner from './components/TrialBanner';

function ProtectedRoute({ children, adminOnly = false, superOnly = false, permArea = null }) {
  const { user, loading, hasPermission } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#F9F8F6]"><div className="w-10 h-10 border-3 border-[#556B2F] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (superOnly && user.role !== 'superadmin') return <Navigate to="/appointments" replace />;
  if (adminOnly && !['admin','superadmin'].includes(user.role)) return <Navigate to="/appointments" replace />;
  if (permArea && user.role === 'staff' && !hasPermission(permArea, 'enabled')) return <Navigate to="/appointments" replace />;
  return children;
}

function AppLayout({ children }) {
  return (
    <LicenceProvider>
    <div className="flex min-h-screen bg-[#F9F8F6]">
      <div className="hidden lg:block"><Sidebar /></div>
      <main className="flex-1 lg:ml-0 overflow-auto">
        <TrialBanner />
        <div className="p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 max-w-7xl mx-auto">{children}</div>
      </main>
    </div>
    </LicenceProvider>
  );
}

function CalendarLayout({ children }) {
  return (
    <LicenceProvider>
    <div className="flex min-h-screen bg-[#F9F8F6]">
      <div className="hidden lg:block"><Sidebar /></div>
      <main className="flex-1 lg:ml-0 overflow-hidden">
        <TrialBanner />
        <div className="p-2 sm:p-3 h-full">{children}</div>
      </main>
    </div>
    </LicenceProvider>
  );
}

function MobileLayout({ children }) {
  return (
    <LicenceProvider>
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="hidden lg:flex min-h-screen">
        <Sidebar />
        <main className="flex-1 overflow-auto">
          <TrialBanner />
          <div className="p-8 max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
      <div className="lg:hidden"><TrialBanner />{children}</div>
    </div>
    </LicenceProvider>
  );
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#F9F8F6]"><div className="w-10 h-10 border-3 border-[#556B2F] border-t-transparent rounded-full animate-spin" /></div>;
  if (user) return <Navigate to={user.role === 'staff' ? '/appointments' : '/dashboard'} replace />;
  return <LandingPage />;
}

function LoginRedirect() {
  const { user } = useAuth();
  if (user) return <Navigate to={user.role === 'staff' ? '/appointments' : '/dashboard'} replace />;
  return <LoginPage />;
}

function AdminLoginRedirect() {
  const { user } = useAuth();
  if (user && user.role === 'superadmin') return <Navigate to="/admin/dashboard" replace />;
  return <AdminLoginPage />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<LoginRedirect />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/register" element={<LoginRedirect />} />
      <Route path="/get-started" element={<GetStartedPage />} />
      <Route path="/client-login" element={<ClientLoginPage />} />
      <Route path="/my-bookings" element={<MyBookingsPage />} />
      <Route path="/book/:slug" element={<PublicBookingPage />} />

      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute adminOnly><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/appointments" element={<ProtectedRoute permArea="calendar"><CalendarLayout><AppointmentsPage /></CalendarLayout></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute permArea="clients"><AppLayout><ClientsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/clients/:clientId" element={<ProtectedRoute permArea="clients"><AppLayout><ClientProfilePage /></AppLayout></ProtectedRoute>} />
      <Route path="/services" element={<ProtectedRoute permArea="services"><AppLayout><ServicesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/staff" element={<ProtectedRoute permArea="team"><AppLayout><StaffPage /></AppLayout></ProtectedRoute>} />
      <Route path="/sales" element={<ProtectedRoute permArea="sales"><AppLayout><SalesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/retention" element={<ProtectedRoute adminOnly><AppLayout><RetentionPage /></AppLayout></ProtectedRoute>} />
      <Route path="/marketing" element={<ProtectedRoute permArea="marketing"><AppLayout><MarketingPage /></AppLayout></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute permArea="reports"><AppLayout><ReportsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute permArea="settings"><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/locations" element={<ProtectedRoute adminOnly><AppLayout><LocationsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/resources" element={<ProtectedRoute adminOnly><AppLayout><ResourcesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/ai-assistant" element={<ProtectedRoute adminOnly><AppLayout><AIAssistantPage /></AppLayout></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><AppLayout><HelpPage /></AppLayout></ProtectedRoute>} />
      <Route path="/more" element={<ProtectedRoute><MobileLayout><MorePage /></MobileLayout></ProtectedRoute>} />
      {/* Super Admin Portal — separate layout, no business sidebar */}
      <Route path="/admin" element={<AdminLoginRedirect />} />
      <Route path="/admin/dashboard" element={<ProtectedRoute superOnly><SuperAdminPage /></ProtectedRoute>} />
      <Route path="/superadmin" element={<ProtectedRoute superOnly><SuperAdminPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ClientAuthProvider>
          <PushNotificationManager />
          <AppRoutes />
          <MobileBottomNavWrapper />
          <Toaster position="top-right" />
          <WhatsAppHelpButton />
          {/* "Made by Apptio LD" branding badge — fixed bottom-LEFT so it never overlaps the
              appointment right-sidebar or its Checkout button. Always click-through. */}
          <ApptioLDBadge />
        </ClientAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function MobileBottomNavWrapper() {
  const { user } = useAuth();
  const location = useLocation();
  const publicPaths = ['/', '/login', '/register', '/get-started', '/client-login', '/my-bookings', '/reset-password', '/admin'];
  const isPublic = publicPaths.includes(location.pathname) || location.pathname.startsWith('/book/');
  if (!user || isPublic) return null;
  return <MobileBottomNav />;
}


function ApptioLDBadge() {
  // Show the marketing badge ONLY on public-facing pages (landing, login, public booking, etc.)
  // Hide on logged-in workspace pages so it never overlaps the right sidebar / Checkout buttons.
  const { user } = useAuth();
  const location = useLocation();
  const publicPaths = ['/', '/login', '/register', '/get-started', '/client-login', '/reset-password'];
  const isPublicPage = publicPaths.includes(location.pathname) || location.pathname.startsWith('/book/');
  if (user && !isPublicPage) return null;
  return (
    <div
      className="hidden lg:inline-flex fixed bottom-4 left-4 z-[10000] items-center justify-center px-4 py-2 rounded-full text-white text-[13px] font-semibold tracking-wide pointer-events-none select-none"
      style={{ background: '#556B2F', fontFamily: 'Outfit, sans-serif' }}
      data-testid="apptio-ld-badge"
    >
      Made by Apptio LD
    </div>
  );
}
